import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { IdleWarningComponent } from '../../global/components/idle-warning/idle-warning.component';
import { HeaderService } from '../../header/header.service';

/**
 * Replaces the legacy ng-idle (AngularJS) integration.
 * Monitors user activity, shows a countdown warning on idle,
 * logs out on timeout, and periodically refreshes the auth token.
 *
 * Config values (idle, timeout, keep_alive_interval) are read from
 * SERVER_CONFIG.configSettings, matching the legacy app.
 */
@Injectable({ providedIn: 'root' })
export class IdleService implements OnDestroy {
  private idleSeconds = 1500;
  private timeoutSeconds = 60;
  private keepAliveIntervalSeconds = 1500;

  private idleTimerId: any = null;
  private timeoutTimerId: any = null;
  private keepAliveTimerId: any = null;

  private countdown = 0;
  private warningDialogRef: MatDialogRef<IdleWarningComponent> | null = null;
  private watching = false;
  private destroyed$ = new Subject<void>();

  private readonly USER_EVENTS = [
    'mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart', 'click'
  ];

  private boundOnUserActivity = this.onUserActivity.bind(this);

  constructor(
    private ngZone: NgZone,
    private http: HttpClient,
    private dialog: MatDialog,
    private router: Router,
    private headerService: HeaderService
  ) {}

  /**
   * Call after SERVER_CONFIG is loaded (from AppComponent on startup).
   */
  configure(configSettings: any): void {
    if (configSettings?.idle) {
      this.idleSeconds = JSON.parse(configSettings.idle);
    }
    if (configSettings?.timeout) {
      this.timeoutSeconds = JSON.parse(configSettings.timeout);
    }
    if (configSettings?.keep_alive_interval) {
      this.keepAliveIntervalSeconds = configSettings.keep_alive_interval;
    }
  }

  watch(): void {
    if (this.watching) {
      return;
    }
    this.watching = true;

    this.ngZone.runOutsideAngular(() => {
      this.USER_EVENTS.forEach((event) =>
        document.addEventListener(event, this.boundOnUserActivity, { passive: true })
      );
    });

    this.startIdleTimer();
    this.startKeepAlive();
  }

  stop(): void {
    this.watching = false;
    this.clearIdleTimer();
    this.clearTimeoutTimer();
    this.clearKeepAlive();
    this.closeWarningDialog();

    this.USER_EVENTS.forEach((event) =>
      document.removeEventListener(event, this.boundOnUserActivity)
    );
  }

  ngOnDestroy(): void {
    this.stop();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private onUserActivity(): void {
    if (!this.watching) {
      return;
    }

    if (this.warningDialogRef) {
      this.ngZone.run(() => this.interruptIdle());
    }

    this.resetIdleTimer();
  }

  private startIdleTimer(): void {
    this.clearIdleTimer();
    this.idleTimerId = setTimeout(() => {
      this.ngZone.run(() => this.onIdleStart());
    }, this.idleSeconds * 1000);
  }

  private resetIdleTimer(): void {
    this.clearIdleTimer();
    this.clearTimeoutTimer();
    this.startIdleTimer();
  }

  private clearIdleTimer(): void {
    if (this.idleTimerId) {
      clearTimeout(this.idleTimerId);
      this.idleTimerId = null;
    }
  }

  /** Fired when user has been idle for the configured duration. */
  private onIdleStart(): void {
    this.countdown = this.timeoutSeconds;
    this.playAlertSound();
    this.showWarningDialog();
    this.startTimeoutCountdown();
  }

  private startTimeoutCountdown(): void {
    this.clearTimeoutTimer();
    this.timeoutTimerId = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        this.ngZone.run(() => this.onTimeout());
      }
    }, 1000);
  }

  private clearTimeoutTimer(): void {
    if (this.timeoutTimerId) {
      clearInterval(this.timeoutTimerId);
      this.timeoutTimerId = null;
    }
  }

  /** User moved/clicked while warning was shown — cancel the timeout. */
  private interruptIdle(): void {
    this.clearTimeoutTimer();
    this.closeWarningDialog();
    this.startIdleTimer();
  }

  /** Timeout expired — log the user out (mirrors legacy IdleTimeout handler). */
  private onTimeout(): void {
    this.clearTimeoutTimer();
    this.closeWarningDialog();

    if (this.isLocalHost()) {
      return;
    }

    this.headerService.signout(environment.base_url).subscribe({
      next: () => {
        sessionStorage.removeItem('Authorization');
        this.router.navigate(['/loggedOut']);
      },
      error: () => {
        sessionStorage.removeItem('Authorization');
        this.router.navigate(['/loggedOut']);
      },
    });
  }

  private isLocalHost(): boolean {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  }

  private showWarningDialog(): void {
    if (this.warningDialogRef) {
      return;
    }
    this.warningDialogRef = this.dialog.open(IdleWarningComponent, {
      disableClose: true,
      width: '400px',
      panelClass: 'modal-warning',
      data: { timeout: this.timeoutSeconds },
    });

    this.warningDialogRef.afterClosed().subscribe(() => {
      this.warningDialogRef = null;
    });
  }

  private closeWarningDialog(): void {
    if (this.warningDialogRef) {
      this.warningDialogRef.close();
      this.warningDialogRef = null;
    }
  }

  private playAlertSound(): void {
    try {
      const audio = new Audio('assets/sound/voice_alert.mp3');
      audio.play().catch(() => {});
    } catch {
      // Audio playback may be blocked by browser autoplay policy
    }
  }

  /** Periodically refresh the identity token (mirrors legacy Keepalive → IdleFactory.refreshToken). */
  private startKeepAlive(): void {
    this.clearKeepAlive();
    this.keepAliveTimerId = setInterval(() => {
      this.refreshToken();
    }, this.keepAliveIntervalSeconds * 1000);
  }

  private clearKeepAlive(): void {
    if (this.keepAliveTimerId) {
      clearInterval(this.keepAliveTimerId);
      this.keepAliveTimerId = null;
    }
  }

  private refreshToken(): void {
    const url = environment.base_url + '/getIdentityToken';
    this.http.get<any>(url, { withCredentials: true }).subscribe({
      next: (response) => {
        if (response?.id_token) {
          const bearerToken = 'Bearer ' + response.id_token;
          sessionStorage.setItem('Authorization', bearerToken);
          this.broadcastTokenRefresh(bearerToken);
        }
      },
      error: () => {},
    });
  }

  private broadcastTokenRefresh(token: string): void {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }
    const channel = new BroadcastChannel('tokenRefresh');
    channel.postMessage(token);
    channel.close();
  }
}
